import { useState, useCallback, useRef } from 'react';
import { registerCard, getBrowserInfo } from '../../../services/mangopay';
import { setStripeState } from '../../../services/stripe';
import callApi from '../../../services/api';

export type PaymentStep = 'idle' | 'validating' | 'validated' | 'confirming';

interface OrderSnapshot {
  order: any;
  billingAddress?: any;
}

interface PaymentFlowState {
  paymentStep: PaymentStep;
  mangopayCustomerId: string | null;
  orderSnapshot: OrderSnapshot | null;
  registeredCard: any | null;
  show3DSModal: boolean;
  redirectUrlState: string | null;
  customerError: string | null;
  waitMessage: string | null;
  popUpWaitTimeIsOpen: boolean;
}

const STORAGE_KEYS = {
  mangopayCustomerId: 'mangopayCustomerId',
  paymentValidated: 'paymentValidated',
  paymentValidatedAt: 'paymentValidatedAt',
  orderSnapshot: 'orderSnapshot',
  meetingInfosFormState: 'meetingInfosFormState',
  correlationId: 'payment3dsCorrelationId',
} as const;

const MAX_3DS_AGE_MS = 5 * 60 * 1000;

const RETURN_PARAM = '3ds';
const RETURN_VALUE = 'return';
const TOKEN_PARAM = 'token';

export interface Restore3DSResult {
  mangopayCustomerId: string;
  orderSnapshot: OrderSnapshot | null;
  formState: {
    formInputs: any;
    formInputsBillingAddress: any;
    areBillingAddressAndRdvAddressIdentical: boolean;
  } | null;
}

export function clearStoredPaymentData() {
  if (typeof window === 'undefined') return;
  Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
}

// React elements carry a Symbol `$$typeof` that JSON.stringify silently drops,
// leaving a plain `{type, key, ref, props, _owner}` object. On restore, rendering
// such a "zombie" as a React child throws React #31. Strip any React element from
// the payload at the write boundary so the bug class cannot re-emerge if a future
// state field starts holding JSX (see meetingInfosData.js label helpers).
const REACT_ELEMENT_TYPE = typeof Symbol === 'function' ? Symbol.for('react.element') : null;
function stripReactElements(_key: string, value: unknown): unknown {
  if (value && typeof value === 'object' && (value as { $$typeof?: unknown }).$$typeof === REACT_ELEMENT_TYPE) {
    return undefined;
  }
  return value;
}

export function isOn3DSReturn(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get(RETURN_PARAM) === RETURN_VALUE;
}

function generateCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function restore3DSState(): Restore3DSResult | null {
  if (typeof window === 'undefined') return null;

  const urlToken = new URLSearchParams(window.location.search).get(TOKEN_PARAM);
  const storedToken = localStorage.getItem(STORAGE_KEYS.correlationId);
  const storedCustomerId = localStorage.getItem(STORAGE_KEYS.mangopayCustomerId);
  const paymentValidated = localStorage.getItem(STORAGE_KEYS.paymentValidated);
  const paymentTimestamp = localStorage.getItem(STORAGE_KEYS.paymentValidatedAt);

  const isRecent = !!paymentTimestamp && (Date.now() - Number(paymentTimestamp)) < MAX_3DS_AGE_MS;
  const tokenMatches = !!urlToken && !!storedToken && urlToken === storedToken;

  if (!storedCustomerId || paymentValidated !== 'pending' || !isRecent || !tokenMatches) {
    clearStoredPaymentData();
    return null;
  }

  const orderSnapshot = safeParse<OrderSnapshot>(localStorage.getItem(STORAGE_KEYS.orderSnapshot));
  const formState = safeParse<Restore3DSResult['formState']>(
    localStorage.getItem(STORAGE_KEYS.meetingInfosFormState),
  );
  clearStoredPaymentData();

  return {
    mangopayCustomerId: storedCustomerId,
    orderSnapshot,
    formState,
  };
}

interface ValidatePaymentArgs {
  cardNumber: string;
  cvv: string;
  expiryMonth: string;
  expiryYear: string;
  customer: any;
  billingAddress: any | undefined;
  createOrderSnapshot: () => OrderSnapshot;
  formStateToStore: {
    formInputs: any;
    formInputsBillingAddress: any;
    areBillingAddressAndRdvAddressIdentical: boolean;
  };
  onCardError: (_error: string) => void;
  onValidated: () => void;
}

interface ConfirmOrderArgs {
  promoCode: { code?: string } | null;
  setStripe: (_stripe: any) => void;
  setCustomerAndOrderId: (_customer: any, _orderId: string) => void;
  trackOrder: (_order: any, _isFirstOrder: boolean, _promoCode?: string) => void;
  onSuccess: () => void;
  onCardError: (_error: string | null) => void;
}

export default function usePaymentFlow() {
  const [state, setState] = useState<PaymentFlowState>({
    paymentStep: 'idle',
    mangopayCustomerId: null,
    orderSnapshot: null,
    registeredCard: null,
    show3DSModal: false,
    redirectUrlState: null,
    customerError: null,
    waitMessage: null,
    popUpWaitTimeIsOpen: false,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  const initFromRestore = useCallback((restored: Restore3DSResult) => {
    setState((prev) => ({
      ...prev,
      paymentStep: 'validated' as PaymentStep,
      mangopayCustomerId: restored.mangopayCustomerId,
      orderSnapshot: restored.orderSnapshot,
    }));
  }, []);

  const validatePaymentInfo = useCallback(({
    cardNumber, cvv, expiryMonth, expiryYear,
    customer, billingAddress,
    createOrderSnapshot, formStateToStore,
    onCardError, onValidated,
  }: ValidatePaymentArgs) => {
    setState((prev) => ({
      ...prev,
      paymentStep: 'validating',
      popUpWaitTimeIsOpen: true,
      waitMessage: 'Enregistrement de vos informations...',
      customerError: null,
    }));

    let customerRegistered = false;
    const correlationId = generateCorrelationId();

    callApi('public/registerCustomerForMangoPay', 'post', { customer, billingAddress })
      .then((res: any) => {
        customerRegistered = true;
        const { customerId } = res;
        setState((prev) => ({
          ...prev,
          mangopayCustomerId: customerId,
          waitMessage: 'Validation de votre carte en cours...',
        }));

        const expiryDate = `${expiryMonth}${expiryYear}`;
        const cardData = { expiryDate, cvv, cardNumber };
        return registerCard(cardData, customerId);
      })
      .then((card: any) => {
        setState((prev) => ({ ...prev, registeredCard: card }));
        const BrowserInfo = getBrowserInfo();
        const returnUrl = `${window.location.origin}${window.location.pathname}?${RETURN_PARAM}=${RETURN_VALUE}&${TOKEN_PARAM}=${correlationId}`;
        return callApi('public/submitCard', 'post', { card, BrowserInfo, returnUrl });
      })
      .then((res: any) => {
        const orderSnapshot = createOrderSnapshot();

        if (res.redirectUrl) {
          const currentState = stateRef.current;
          localStorage.setItem(STORAGE_KEYS.mangopayCustomerId, currentState.mangopayCustomerId!);
          localStorage.setItem(STORAGE_KEYS.paymentValidated, 'pending');
          localStorage.setItem(STORAGE_KEYS.paymentValidatedAt, String(Date.now()));
          localStorage.setItem(STORAGE_KEYS.orderSnapshot, JSON.stringify(orderSnapshot));
          localStorage.setItem(STORAGE_KEYS.meetingInfosFormState, JSON.stringify(formStateToStore, stripReactElements));
          localStorage.setItem(STORAGE_KEYS.correlationId, correlationId);

          setState((prev) => ({
            ...prev,
            show3DSModal: true,
            redirectUrlState: res.redirectUrl,
            popUpWaitTimeIsOpen: false,
          }));
          setTimeout(() => {
            if (typeof window !== 'undefined') window.location.href = res.redirectUrl;
          }, 5000);
        } else {
          setState((prev) => ({
            ...prev,
            paymentStep: 'validated',
            popUpWaitTimeIsOpen: false,
            orderSnapshot,
          }));
          onValidated();
        }
      })
      .catch((response: any) => {
        const errorMsg = response?.error || 'Une erreur est survenue lors de la validation du paiement';
        if (customerRegistered) {
          onCardError(errorMsg);
        }
        setState((prev) => ({
          ...prev,
          paymentStep: 'idle',
          popUpWaitTimeIsOpen: false,
          ...(!customerRegistered && { customerError: errorMsg }),
        }));
        if (typeof Raven !== 'undefined') {
          (Raven as any).captureException(JSON.stringify(response));
        } else {
          console.error(response);
        }
      });
  }, []);

  const confirmOrder = useCallback(({
    promoCode, setStripe, setCustomerAndOrderId, trackOrder, onSuccess, onCardError,
  }: ConfirmOrderArgs) => {
    const { mangopayCustomerId, orderSnapshot } = stateRef.current;

    setState((prev) => ({
      ...prev,
      paymentStep: 'confirming',
      popUpWaitTimeIsOpen: true,
      waitMessage: 'Confirmation de la commande en cours...',
    }));

    callApi('public/createOrderForRegisteredCustomer', 'post', {
      customerId: mangopayCustomerId,
      order: orderSnapshot!.order,
      billingAddress: orderSnapshot!.billingAddress,
    })
      .then((res: any) => {
        trackOrder(res.order, res.isFirstOrder, promoCode?.code);
        setCustomerAndOrderId(res.customer, res.order.id);
        return res;
      })
      .then((response: any) => setStripeState({ response, setStripe }))
      .then(() => {
        onSuccess();
      })
      .catch((response: any) => {
        setState((prev) => ({
          ...prev,
          paymentStep: 'validated',
          popUpWaitTimeIsOpen: false,
        }));
        onCardError(response?.error || null);
        if (typeof Raven !== 'undefined') {
          (Raven as any).captureException(JSON.stringify(response));
        } else {
          console.error(response);
        }
      });
  }, []);

  const resetPaymentValidation = useCallback(() => {
    setState((prev) => ({
      ...prev,
      paymentStep: 'idle',
      mangopayCustomerId: null,
      registeredCard: null,
      orderSnapshot: null,
      customerError: null,
    }));
  }, []);

  const closeModal = useCallback(() => {
    setState((prev) => ({ ...prev, show3DSModal: false }));
  }, []);

  return {
    ...state,
    initFromRestore,
    validatePaymentInfo,
    confirmOrder,
    resetPaymentValidation,
    closeModal,
  };
}
